import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn, LogOut, X } from 'lucide-react';
import Header from '@/components/Header';

const BACKEND = "https://ashokahospitalhubli.com/api";
axios.defaults.withCredentials = true;

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const [banner, setBanner] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(false);

  // ---------------- CHECK AUTH ----------------
  const checkAuth = async () => {
    try {
      const res = await axios.get(`${BACKEND}/admin/me`);
      if (res.status === 200) setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    }
  };

  // ---------------- FETCH BANNER ----------------
  const fetchBanner = async () => {
    try {
      const res = await axios.get(`${BACKEND}/banner`);
      setBanner(res.data);
    } catch {
      setBanner(null);
    }
  };

  useEffect(() => {
    checkAuth();
    fetchBanner();
  }, []);

  // ---------------- LOGIN ----------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/admin/login`, loginData);
      toast.success("Login successful!");
      setIsAuthenticated(true);
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- LOGOUT ----------------
  const handleLogout = async () => {
    await axios.post(`${BACKEND}/admin/logout`);
    toast.success("Logged out");
    setIsAuthenticated(false);
  };

  // ---------------- ADD / REPLACE BANNER ----------------
  const handleAddBanner = async () => {
    if (!bannerImage) {
      toast.error("Select banner image");
      return;
    }

    const formData = new FormData();
    formData.append("image", bannerImage);

    try {
      setBannerLoading(true);
      await axios.post(`${BACKEND}/admin/banner`, formData);
      toast.success("Banner updated");
      fetchBanner();
      setBannerImage(null);
    } catch {
      toast.error("Failed to upload banner");
    } finally {
      setBannerLoading(false);
    }
  };

  // ---------------- DELETE BANNER ----------------
  const handleDeleteBanner = async () => {
    try {
      await axios.delete(`${BACKEND}/admin/banner`);
      toast.success("Banner removed");
      setBanner(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---------------- LOGIN PAGE ----------------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50">
        <Header />
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center pt-20 px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <LogIn className="mx-auto mb-4 text-green-600" size={40} />
              <h2 className="text-3xl font-bold">Admin Login</h2>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <input
                type="text"
                placeholder="Username"
                value={loginData.username}
                onChange={(e)=>setLoginData({...loginData, username:e.target.value})}
                className="w-full p-3 border rounded-lg"
                required
              />

              <input
                type="password"
                placeholder="Password"
                value={loginData.password}
                onChange={(e)=>setLoginData({...loginData, password:e.target.value})}
                className="w-full p-3 border rounded-lg"
                required
              />

              <button
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-lg"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- ADMIN DASHBOARD ----------------
  return (
    <div className="min-h-screen bg-gray-50 p-8">

      <div className="flex justify-between items-center mb-10">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>

        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg"
        >
          <LogOut size={18}/> Logout
        </button>
      </div>

      {/* BANNER SECTION */}
      <div className="max-w-2xl bg-white p-8 rounded-2xl shadow-lg">
        <h2 className="text-2xl font-semibold mb-4">Website Banner</h2>

        {/* EXISTING BANNER */}
        {banner && (
          <div className="relative mb-4">
            <img
              src={`https://ashokahospitalhubli.com${banner.image}`}
              className="w-full h-56 object-cover rounded-xl"
            />

            <button
              onClick={handleDeleteBanner}
              className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* UPLOAD */}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setBannerImage(e.target.files[0])}
          className="w-full mb-3"
        />

        <button
          onClick={handleAddBanner}
          disabled={bannerLoading}
          className="w-full bg-orange-500 text-white py-3 rounded-lg"
        >
          {bannerLoading ? "Uploading..." : banner ? "Replace Banner" : "Add Banner"}
        </button>
      </div>

    </div>
  );
};

export default AdminPanel;